// FassalkartAI — Real-time Notification Bell
// Reads from ki_notifications collection written by dailyFarmerAlerts Cloud Function.
// Schema: { userId, type, title, message, severity, read, createdAt }

import React, { useState, useEffect } from "react";
import {
  IconButton, Badge, Popover,
  List, ListItem, ListItemText,
  Typography, Box, Divider, Chip, Button
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection, query, where,
  orderBy, limit, onSnapshot,
  doc, updateDoc, writeBatch
} from "firebase/firestore";

// Cloud Function uses severity: "high" | "medium" | "info"
const SEVERITY_COLOR = {
  high:   "error",
  medium: "warning",
  info:   "info",
  low:    "info",
};

const SEVERITY_EMOJI = {
  high:   "🚨",
  medium: "⚠️",
  info:   "ℹ️",
  low:    "📋",
};

export default function NotificationBell() {
  const { currentUser } = useAuth();
  const [notifs, setNotifs]   = useState([]);
  const [anchor, setAnchor]   = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const q = query(
      collection(db, "ki_notifications"),
      where("userId", "==", currentUser.uid),
      where("read",   "==", false),
      orderBy("createdAt", "desc"),
      limit(20)
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = [];
      snap.forEach(d => data.push({ id: d.id, ...d.data() }));
      setNotifs(data);
    }, () => {});

    return unsub;
  }, [currentUser?.uid]);

  const markOne = (id) => {
    updateDoc(doc(db, "ki_notifications", id), { read: true }).catch(() => {});
  };

  const markAllRead = async () => {
    if (!notifs.length) return;
    try {
      const batch = writeBatch(db);
      notifs.forEach(n =>
        batch.update(doc(db, "ki_notifications", n.id), { read: true })
      );
      await batch.commit();
    } catch {}
    setAnchor(null);
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1)  return "अभी";
    if (diffMin < 60) return `${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24)   return `${diffH}h`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          color: "#546E7A",
          "&:hover": { color: "#2E7D32", bgcolor: "rgba(46,125,50,0.08)" },
          borderRadius: "10px",
          width: 36,
          height: 36,
        }}
      >
        <Badge badgeContent={notifs.length || null} color="error" max={9}>
          <NotificationsOutlinedIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top",    horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            maxHeight: 480,
            borderRadius: "14px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          background: "linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)",
        }}>
          <Typography fontWeight={700} fontSize={14}>
            🔔 Alerts
            {notifs.length > 0 && (
              <Chip
                label={`${notifs.length} new`}
                size="small"
                sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 600 }}
              />
            )}
          </Typography>
          {notifs.length > 0 && (
            <Button size="small" onClick={markAllRead}
              sx={{ fontSize: 11, color: "#2E7D32", textTransform: "none", minWidth: 0 }}>
              Clear all
            </Button>
          )}
        </Box>

        {/* Body */}
        {notifs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Box component="img" src="/logo192.png" alt="FassalkartAI" sx={{ width: 40, height: 40, objectFit: 'contain', mb: 0.5 }} />
            <Typography color="text.secondary" fontSize={13}>
              No new alerts
            </Typography>
            <Typography color="text.secondary" fontSize={11} mt={0.5}>
              Daily alerts arrive at 6 AM IST
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflowY: "auto", maxHeight: 390 }}>
            {notifs.map((n, idx) => {
              const sev = n.severity || "info";
              return (
                <React.Fragment key={n.id}>
                  <ListItem
                    alignItems="flex-start"
                    onClick={() => markOne(n.id)}
                    sx={{
                      cursor: "pointer",
                      px: 2, py: 1.2,
                      "&:hover": { bgcolor: "rgba(46,125,50,0.04)" },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.4 }}>
                          <Typography fontSize={13} fontWeight={700} sx={{ flex: 1 }}>
                            {SEVERITY_EMOJI[sev]} {n.title || "Alert"}
                          </Typography>
                          <Typography fontSize={10} color="text.disabled" sx={{ flexShrink: 0 }}>
                            {formatTime(n.createdAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            fontSize={12}
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.5 }}
                          >
                            {n.message}
                          </Typography>
                          <Chip
                            label={sev}
                            size="small"
                            color={SEVERITY_COLOR[sev] || "default"}
                            sx={{ mt: 0.5, height: 16, fontSize: "0.6rem" }}
                          />
                        </>
                      }
                    />
                  </ListItem>
                  {idx < notifs.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
